// src/createChainOfResponsibility.tsx
import PropTypes from "prop-types";
import React, {
  createContext,
  isValidElement,
  memo,
  useCallback,
  useContext,
  useMemo
} from "react";

// src/isReactComponent.ts
function isConsumer(component) {
  var _a;
  return ((_a = component == null ? void 0 : component.$$typeof) == null ? void 0 : _a.toString()) === "Symbol(react.context)";
}
function isProvider(component) {
  var _a;
  return ((_a = component == null ? void 0 : component.$$typeof) == null ? void 0 : _a.toString()) === "Symbol(react.provider)";
}
function isFragment(component) {
  return (component == null ? void 0 : component.toString()) === "Symbol(react.fragment)";
}
function isFunctionComponent(component) {
  if (typeof component === "function") {
    return true;
  }
  return isPureFunctionComponent(component);
}
function isPureFunctionComponent(component) {
  var _a;
  return ((_a = component == null ? void 0 : component.$$typeof) == null ? void 0 : _a.toString()) === "Symbol(react.memo)" && isFunctionComponent(component.type);
}
function isComponentClass(component) {
  return typeof component === "object" && typeof (component == null ? void 0 : component["render"]) === "function";
}
function isReactComponent(component) {
  return isFunctionComponent(component) || isComponentClass(component) || isFragment(component) || isConsumer(component) || isProvider(component);
}

// src/private/compose.ts
function compose(...fns) {
  return (fn) => fns.reduce((chain, fn2) => fn2(chain), fn);
}

// src/private/applyMiddleware.ts
function applyMiddleware(...arrayOfMiddleware) {
  return (...init) => {
    const chain = arrayOfMiddleware.map((middleware) => middleware(...init));
    return compose(...chain);
  };
}

// src/createChainOfResponsibility.tsx
function createChainOfResponsibility(options = {}) {
  const defaultUseBuildComponentCallback = {
    get enhancer() {
      return void 0;
    },
    useBuildComponentCallback(_, options2) {
      if (options2 == null ? void 0 : options2.fallbackComponent) {
        return options2.fallbackComponent;
      }
      throw new Error("This component/hook cannot be used outside of its corresponding <Provider>");
    }
  };
  const context = createContext(defaultUseBuildComponentCallback);
  const Provider = ({ children, init, middleware }) => {
    if (!Array.isArray(middleware) || middleware.some((middleware2) => typeof middleware2 !== "function")) {
      throw new Error("middleware prop must be an array of functions");
    }
    const patchedMiddleware = Object.freeze(
      middleware ? middleware.map((fn) => (init2) => {
        const enhancer2 = fn(init2);
        return (next) => (originalRequest) => {
          let hasReturned;
          const returnValue = enhancer2((nextRequest) => {
            if (hasReturned) {
              throw new Error("next() cannot be called after the function had returned synchronously");
            }
            !options.passModifiedRequest && nextRequest !== originalRequest && console.warn(
              'react-chain-of-responsibility: "options.passModifiedRequest" must be set to true to pass a different request object to next().'
            );
            return next(options.passModifiedRequest ? nextRequest : originalRequest);
          })(originalRequest);
          hasReturned = true;
          if (isValidElement(returnValue)) {
            throw new Error("middleware must not return React element directly");
          } else if (returnValue !== false && returnValue !== null && typeof returnValue !== "undefined" && !isReactComponent(returnValue)) {
            throw new Error(
              "middleware must return false, null, undefined, function component, or class component"
            );
          }
          return returnValue;
        };
      }) : []
    );
    const { enhancer: parentEnhancer } = useContext(context);
    const enhancer = useMemo(
      () => (
        // We are reversing because it is easier to read:
        // - With reverse, [a, b, c] will become a(b(c(fn)))
        // - Without reverse, [a, b, c] will become c(b(a(fn)))
        applyMiddleware(
          ...[...patchedMiddleware, ...parentEnhancer ? [() => parentEnhancer] : []].reverse()
        )(init)
      ),
      [init, middleware, parentEnhancer]
    );
    const useBuildComponentCallback2 = useCallback(
      (request, options2 = {}) => enhancer(() => options2.fallbackComponent)(request),
      [enhancer]
    );
    const contextValue = useMemo(
      () => ({ enhancer, useBuildComponentCallback: useBuildComponentCallback2 }),
      [enhancer, useBuildComponentCallback2]
    );
    return /* @__PURE__ */ React.createElement(context.Provider, { value: contextValue }, children);
  };
  Provider.displayName = "ChainOfResponsibilityProvider";
  Provider.propTypes = {
    children: PropTypes.any,
    init: PropTypes.any,
    middleware: PropTypes.any
  };
  const useBuildComponentCallback = () => useContext(context).useBuildComponentCallback;
  const Proxy = memo(
    // False positive: "children" is not a prop.
    // eslint-disable-next-line react/prop-types
    ({ children, fallbackComponent, request, ...props }) => {
      const enhancer = useBuildComponentCallback();
      const Component = enhancer(request, { fallbackComponent });
      return Component ? /* @__PURE__ */ React.createElement(Component, { ...props }, children) : null;
    }
  );
  Proxy.displayName = "Proxy";
  Proxy.propTypes = {
    fallbackComponent: PropTypes.any,
    request: PropTypes.any
  };
  return {
    Provider,
    Proxy,
    types: {
      init: void 0,
      middleware: void 0,
      props: void 0,
      request: void 0
    },
    useBuildComponentCallback
  };
}

export {
  createChainOfResponsibility
};
//# sourceMappingURL=chunk-QOVF7TZ6.mjs.map